// 変身テンプレ作成（リデザイン版 / Turn 19a UI 先行）
// 3 アップロード（自撮り動画 + 任意背景 + 任意参照画像）
// + 4 モードタブ（🖼️ 画像 / 📝 テキスト / 👗 服装 / 🎂 加齢）
// + 任意プロンプト + 動画設定 + sticky 生成ボタン + ギャラリーリンク
import { useCallback, useEffect, useRef, useState } from "react";
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  Check,
  History,
  ImageIcon,
  Loader2,
  Settings2,
  Sparkles,
  Video,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useCredits } from "@/hooks/useCredits";
import {
  calculateStep2Credits,
  getStep1CreditsForMode,
  type TemplateDuration,
  type TemplateResolution,
} from "@/lib/templatePricing";
import { track } from "@/lib/analytics";
import { extractFrameFromVideo } from "@/lib/videoFrameExtractor";
import {
  RecreateFromFreeformHistoryModal,
  type FreeformFilter,
} from "@/components/RecreateFromFreeformHistoryModal";
import {
  BackgroundModeSelector,
  type BackgroundMode,
} from "@/components/template/BackgroundModeSelector";
import { VoiceChangeOption } from "@/components/voice/VoiceChangeOption";

import {
  isQuickStartMode,
  resolveSampleUrl,
  SAMPLE_DEFAULTS,
  type QuickStartMode,
} from "@/lib/sampleDefaults";


export const Route = createFileRoute("/_authenticated/create/template/")({
  head: () => ({
    meta: [
      { title: "新しい変身を作る | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  validateSearch: (
    search: Record<string, unknown>,
  ): { from?: string; mode?: QuickStartMode; sample?: 1 } => ({
    from: typeof search.from === "string" ? search.from : undefined,
    mode: isQuickStartMode(search.mode) ? search.mode : undefined,
    sample:
      search.sample === 1 || search.sample === "1" ? (1 as const) : undefined,
  }),

  component: TemplateCreatePage,
});

const VIDEO_TYPES = ["video/mp4", "video/quicktime"];
const VIDEO_MAX = 100 * 1024 * 1024;
const VIDEO_MAX_DURATION = 15;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const IMAGE_MAX = 20 * 1024 * 1024;

const FREE_TEXT_MAX = 500;
const FREE_TEXT_MIN = 5;
const PROMPT_MAX = 300;

// モード定義（Step1 クレジット内訳の概算表示用）
// pricing は templatePricing.ts と整合（Step1=10cr 固定 + Step2 動画レート）
type TransformMode = "image" | "text" | "outfit" | "age" | "background";

const MODE_DEFS: { id: TransformMode; label: string; icon: string; hint: string }[] = [
  { id: "image", label: "画像", icon: "🖼️", hint: "参照画像で別人に" },
  { id: "text", label: "テキスト", icon: "📝", hint: "ことばで自由に" },
  { id: "outfit", label: "服装", icon: "👗", hint: "服だけ変える" },
  { id: "age", label: "加齢", icon: "🎂", hint: "年齢を変える" },
  { id: "background", label: "背景", icon: "🏞️", hint: "背景だけ変える" },
];

const BACKGROUND_PRESETS: { key: string; label: string; description: string }[] = [
  { key: "beach", label: "🏖️ 海・ビーチ", description: "a tropical beach at golden hour with turquoise ocean waves, white sand, and palm trees softly blurred in the background" },
  { key: "sakura", label: "🌸 桜並木", description: "a beautiful Japanese cherry blossom street in full bloom with pink sakura petals falling, soft spring sunlight" },
  { key: "cafe", label: "☕ おしゃれカフェ", description: "a stylish modern cafe interior with warm wood tones, soft natural window light, plants, and softly blurred coffee equipment" },
  { key: "city_night", label: "🌃 夜景", description: "a stunning night cityscape with skyscrapers and bokeh city lights, neon reflections, cinematic lighting" },
  { key: "snow", label: "⛄ 雪景色", description: "a serene snowy winter landscape with snow-covered trees and soft falling snowflakes, cold blue ambient light" },
  { key: "office", label: "🏢 オフィス", description: "a clean modern office interior with bright natural light, glass walls, plants, and softly blurred desks in the background" },
  { key: "studio", label: "📸 スタジオ", description: "a professional photo studio with a clean seamless white background and soft studio lighting" },
  { key: "space", label: "🌌 宇宙", description: "outer space with stars, distant galaxies, and a glowing nebula in soft cinematic lighting" },
  { key: "forest", label: "🌳 森", description: "a lush green forest with sunlight filtering through tall trees, soft natural light, dreamy atmosphere" },
  { key: "shrine", label: "⛩️ 神社", description: "a traditional Japanese shrine with vermillion torii gates, stone lanterns, and soft natural light" },
];

// 加齢チップ：実年齢 -10 〜 +60（5刻み）。実年齢が未入力なら空配列を返す。
function getAgeOptions(currentAge: number | null): number[] {
  if (!currentAge || currentAge <= 0) return [];
  const start = Math.max(0, Math.floor((currentAge - 10) / 5) * 5);
  const end = currentAge + 60;
  const out: number[] = [];
  for (let a = start; a <= end; a += 5) out.push(a);
  return out;
}

type Asset = {
  path: string;
  signedUrl: string;
  name: string;
  size: number;
};
type SelfieAsset = Asset & {
  framePath: string;
  frameSignedUrl: string;
  duration: number;
};

const SESSION_KEY = "create:template:v2";

type Persisted = {
  mode: TransformMode;
  resolution: TemplateResolution;
  duration: TemplateDuration;
  audio: boolean;
  freeText: string;
  prompt: string; // 任意プロンプト（image/outfit/age 用）
  currentAge: number | null; // age モード：実年齢
  targetAge: number; // age モード：なりたい年齢
  backgroundMode: BackgroundMode | null;
  title: string; // 動画の名前
};

function defaultPersisted(): Persisted {
  return {
    mode: "text",
    resolution: "1080p",
    duration: 5,
    audio: false,
    freeText: "",
    prompt: "",
    currentAge: null,
    targetAge: 60,
    backgroundMode: null,
    title: "",
  };
}
function readPersisted(): Persisted {
  if (typeof window === "undefined") return defaultPersisted();
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return defaultPersisted();
    return { ...defaultPersisted(), ...JSON.parse(raw) };
  } catch {
    return defaultPersisted();
  }
}
function writePersisted(s: Persisted) {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
}
function fmtBytes(b: number) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function isSettingsCustomized(p: Persisted) {
  const d = defaultPersisted();
  return (
    p.resolution !== d.resolution ||
    p.duration !== d.duration ||
    p.audio !== d.audio
  );
}

function TemplateCreatePage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/_authenticated/create/template/" });
  const [userId, setUserId] = useState<string | null>(null);
  const [selfie, setSelfie] = useState<SelfieAsset | undefined>();
  const [background, setBackground] = useState<Asset | undefined>();
  const [reference, setReference] = useState<Asset | undefined>();
  const [persisted, setPersisted] = useState<Persisted>(defaultPersisted());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyFilter, setHistoryFilter] = useState<FreeformFilter>("text");
  const [prefilling, setPrefilling] = useState(false);
  const [sampleFlags, setSampleFlags] = useState<{
    reference?: boolean;
    freeText?: boolean;
    prompt?: boolean;
    age?: boolean;
  }>({});
  const [coachmarkVisible, setCoachmarkVisible] = useState(false);
  const [coachmarkMode, setCoachmarkMode] = useState<string | null>(null);
  const balance = useCredits(userId);

  useEffect(() => {
    setPersisted(readPersisted());
    void supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
  }, []);

  const updatePersisted = useCallback((next: Partial<Persisted>) => {
    setPersisted((prev) => {
      const merged = { ...prev, ...next };
      writePersisted(merged);
      return merged;
    });
  }, []);

  // 過去履歴から pre-fill（全モード対応）
  const prefillFromGeneration = useCallback(
    async (id: string) => {
      setPrefilling(true);
      try {
        const { data, error } = await supabase
          .from("generations")
          .select(
            "category_id, category_params, input_video_url, selfie_frame_url, video_resolution, video_duration_requested, video_with_audio",
          )
          .eq("id", id)
          .maybeSingle();
        if (error) throw error;
        if (!data) {
          toast.error("履歴が見つかりませんでした");
          return;
        }
        const cp = (data.category_params ?? {}) as Record<string, unknown>;
        const cid = (data.category_id ?? "") as string;

        // category_id からモード逆引き
        let mode: TransformMode = "text";
        if (cid === "freeform_image") mode = "image";
        else if (cid === "freeform_outfit") mode = "outfit";
        else if (cid === "freeform_age") mode = "age";
        else if (cid === "freeform_background") mode = "background";
        else if (cid === "freeform_text" || cid === "freeform") mode = "text";

        const txt =
          typeof cp.transform_text === "string"
            ? cp.transform_text
            : typeof cp.free_text === "string"
              ? cp.free_text
              : "";
        const optionalPrompt =
          typeof cp.optional_prompt === "string" ? cp.optional_prompt : "";
        const targetAge =
          typeof cp.transform_age === "number"
            ? cp.transform_age
            : typeof cp.transform_age === "string"
              ? Number(cp.transform_age) || 60
              : 60;

        // background_mode 復元（後方互換：無ければ background_image_url の有無で判定）
        const rawBgMode = cp.background_mode;
        const bgUrlForMode =
          typeof cp.background_image_url === "string" ? cp.background_image_url : null;
        const backgroundMode: BackgroundMode | null =
          rawBgMode === "preserve" || rawBgMode === "auto" || rawBgMode === "custom"
            ? rawBgMode
            : bgUrlForMode
              ? "custom"
              : "auto";

        updatePersisted({
          mode,
          freeText: txt,
          prompt: optionalPrompt,
          targetAge,
          backgroundMode,
          resolution:
            (data.video_resolution as TemplateResolution) ?? persisted.resolution,
          duration:
            ((data.video_duration_requested as TemplateDuration) ??
              persisted.duration) as TemplateDuration,
          audio: data.video_with_audio ?? persisted.audio,
        });

        if (data.input_video_url && data.selfie_frame_url) {
          setSelfie({
            path: "",
            signedUrl: data.input_video_url,
            framePath: "",
            frameSignedUrl: data.selfie_frame_url,
            name: "（過去の動画）",
            size: 0,
            duration: 0,
          });
        }

        // 参照画像 / 背景画像の復元
        const refUrl =
          typeof cp.transform_target_image_url === "string"
            ? cp.transform_target_image_url
            : null;
        const bgUrl =
          typeof cp.background_image_url === "string"
            ? cp.background_image_url
            : null;
        if (refUrl) {
          setReference({
            path: "",
            signedUrl: refUrl,
            name: "（過去の参照画像）",
            size: 0,
          });
        }
        if (bgUrl) {
          setBackground({
            path: "",
            signedUrl: bgUrl,
            name: "（過去の背景）",
            size: 0,
          });
        }
        toast.success("過去の設定を読み込みました");
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "読み込みに失敗しました");
      } finally {
        setPrefilling(false);
      }
    },
    [persisted.audio, persisted.duration, persisted.resolution, updatePersisted],
  );

  useEffect(() => {
    if (!search.from || !userId) return;
    const id = search.from;
    void prefillFromGeneration(id).then(() => {
      navigate({ to: "/create/template", search: {}, replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.from, userId]);

  // Quick-start prefill: /create/template?mode=X&sample=1 from dashboard
  useEffect(() => {
    if (!search.mode) return;
    const mode = search.mode as keyof typeof SAMPLE_DEFAULTS;
    const withSample = search.sample === 1;

    // モードは常に適用
    updatePersisted({ mode, backgroundMode: "auto" });

    if (withSample) {
      const def = SAMPLE_DEFAULTS[mode];
      const next: Partial<Persisted> = {};
      const flags: typeof sampleFlags = {};
      if (def.freeText !== undefined) {
        next.freeText = def.freeText;
        flags.freeText = true;
      }
      if (def.prompt !== undefined) {
        next.prompt = def.prompt;
        flags.prompt = true;
      }
      if (def.currentAge !== undefined) {
        next.currentAge = def.currentAge;
        flags.age = true;
      }
      if (def.targetAge !== undefined) next.targetAge = def.targetAge;
      if (Object.keys(next).length > 0) updatePersisted(next);
      if (def.referenceImage) {
        setReference({
          path: "",
          signedUrl: resolveSampleUrl(def.referenceImage.publicPath),
          name: def.referenceImage.displayName,
          size: 0,
        });
        flags.reference = true;
      }
      setSampleFlags(flags);

      // コーチマーク：同モードの再表示を sessionStorage で抑制
      const key = `kagem_coachmark_dismissed_${mode}`;
      const dismissed =
        typeof window !== "undefined" && sessionStorage.getItem(key) === "1";
      if (!dismissed) {
        setCoachmarkMode(mode);
        setCoachmarkVisible(true);
      }
    }

    // URL params をクリア（リロード時の再プリフィル防止 / 自然遷移と同等の状態に）
    navigate({ to: "/create/template", search: {}, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.mode, search.sample]);

  // selfie アップロード完了でコーチマーク自動消滅
  useEffect(() => {
    if (selfie && coachmarkVisible) setCoachmarkVisible(false);
  }, [selfie, coachmarkVisible]);

  const dismissCoachmark = useCallback(() => {
    setCoachmarkVisible(false);
    if (coachmarkMode && typeof window !== "undefined") {
      sessionStorage.setItem(`kagem_coachmark_dismissed_${coachmarkMode}`, "1");
    }
  }, [coachmarkMode]);


  const step1Credits = getStep1CreditsForMode(persisted.mode);
  const step2Credits = calculateStep2Credits(
    persisted.resolution,
    persisted.duration,
    persisted.audio,
  );
  const totalEstimate = step1Credits + step2Credits;

  // モード別の入力バリデーション
  const trimmedFreeText = persisted.freeText.trim();
  const validation = (() => {
    if (!selfie) return { ok: false, reason: "自撮り動画をアップロードしてください" };
    if (persisted.mode === "text") {
      if (trimmedFreeText.length < FREE_TEXT_MIN)
        return { ok: false, reason: `${FREE_TEXT_MIN}文字以上で入力してください` };
      if (persisted.freeText.length > FREE_TEXT_MAX)
        return { ok: false, reason: `${FREE_TEXT_MAX}文字以内で入力してください` };
    }
    if (persisted.mode === "image" && !reference)
      return { ok: false, reason: "なりたい人の画像をアップロードしてください" };
    if (persisted.mode === "outfit" && !reference)
      return { ok: false, reason: "参考にする服装画像をアップロードしてください" };
    if (persisted.mode === "age") {
      if (!persisted.currentAge || persisted.currentAge <= 0)
        return { ok: false, reason: "実年齢を入力してください" };
      const opts = getAgeOptions(persisted.currentAge);
      if (!opts.includes(persisted.targetAge))
        return { ok: false, reason: "年齢を選んでください" };
    }
    if (persisted.mode === "background") {
      // 背景画像 OR 説明テキスト のいずれか必須
      if (!background && trimmedFreeText.length < 2) {
        return { ok: false, reason: "背景画像をアップロード、またはプリセット/テキストで背景を指定してください" };
      }
      if (persisted.freeText.length > FREE_TEXT_MAX)
        return { ok: false, reason: `${FREE_TEXT_MAX}文字以内で入力してください` };
      // 背景モードでは BackgroundModeSelector を出さないので backgroundMode 必須チェックはスキップ
      return { ok: true as const, reason: "" };
    }
    if (!persisted.backgroundMode)
      return { ok: false, reason: "背景の扱いを選んでください" };
    if (persisted.backgroundMode === "custom" && !background)
      return { ok: false, reason: "背景画像をアップロードしてください" };
    return { ok: true as const, reason: "" };
  })();
  const canSubmit = !!userId && validation.ok && !submitting;
  const settingsCustomized = isSettingsCustomized(persisted);

  function getDefaultTitle(): string {
    const now = new Date();
    return now.toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  const handleSubmit = async () => {
    if (!canSubmit || !selfie) return;
    if (balance !== null && balance < totalEstimate) {
      toast.error(`動画生成には ${totalEstimate}cr 必要です（残高 ${balance}cr）`);
      return;
    }

    setSubmitting(true);
    try {
      const trimmedPrompt = persisted.prompt.trim();
      const finalTitle = persisted.title.trim() || getDefaultTitle();
      track("template_step1_start", {
        resolution: persisted.resolution,
        duration: persisted.duration,
        audio: persisted.audio,
        category_id: `freeform_${persisted.mode}`,
        category_params_summary:
          persisted.mode === "text"
            ? `text:${trimmedFreeText.length}chars`
            : persisted.mode === "age"
              ? `age:${persisted.targetAge}`
              : `${persisted.mode}:${reference ? "ref" : "no-ref"}`,
      });
      // 背景モードでは backgroundMode を自動決定（画像があれば custom、なければ auto）
      const effectiveBgMode =
        persisted.mode === "background"
          ? background
            ? "custom"
            : "auto"
          : persisted.backgroundMode;
      const { data, error } = await supabase.functions.invoke(
        "create-template-step1",
        {
          body: {
            selfie_video_url: selfie.signedUrl,
            selfie_frame_url: selfie.frameSignedUrl,
            background_mode: effectiveBgMode,
            background_url:
              effectiveBgMode === "auto"
                ? null
                : (background?.signedUrl ?? null),
            transform_mode: persisted.mode,
            ...(persisted.mode === "text" || persisted.mode === "background"
              ? { transform_text: trimmedFreeText }
              : {}),
            ...(persisted.mode === "age"
              ? { transform_age: persisted.targetAge }
              : {}),
            ...(persisted.mode !== "background" && reference?.signedUrl
              ? { transform_target_image_url: reference.signedUrl }
              : {}),
            ...(persisted.mode !== "text" && persisted.mode !== "background" && trimmedPrompt
              ? { optional_prompt: trimmedPrompt }
              : {}),
            video_resolution: persisted.resolution,
            video_duration: persisted.duration,
            video_with_audio: persisted.audio,
            aspect_ratio: "9:16",
            title: finalTitle,
          },
        },
      );
      if (error) {
        let body: any = null;
        try { body = await (error as any).context?.json?.(); } catch { /* ignore */ }
        if (body?.error === "CONTENT_BLOCKED") {
          toast.error("不適切なコンテンツが検出されたため生成できません。別の素材でお試しください。");
          setSubmitting(false);
          setConfirmOpen(false);
          return;
        }
        if (body?.error === "MODERATION_UNAVAILABLE") {
          toast.error("コンテンツ確認サービスが一時停止中です。時間を置いてお試しください。");
          setSubmitting(false);
          setConfirmOpen(false);
          return;
        }
        throw new Error(body?.error ? `${body.error}: ${body.detail ?? ""}` : (error as Error).message);
      }
      const genId = (data as { generation_id?: string })?.generation_id;
      if (!genId) throw new Error("生成IDが取得できませんでした");
      navigate({
        to: "/create/template/$id/step1-processing",
        params: { id: genId },
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "送信に失敗しました");
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };


  const showReferenceSlot = persisted.mode === "image" || persisted.mode === "outfit" || persisted.mode === "age";
  const referenceRequired = persisted.mode === "image" || persisted.mode === "outfit";

  return (
    <main className="relative min-h-screen overflow-hidden bg-background pb-32">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
      >
        <div className="absolute -top-32 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-primary/20 opacity-40 blur-[120px]" />
        <div className="absolute top-1/3 -right-32 h-[360px] w-[360px] rounded-full bg-fuchsia-500/10 blur-[100px]" />
        <div className="absolute bottom-0 -left-24 h-[360px] w-[360px] rounded-full bg-indigo-500/10 blur-[100px]" />
      </div>

      <div className="mx-auto max-w-3xl px-4 pt-6 sm:pt-10">
        <Link
          to="/create"
          className="group mb-5 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          新規作成に戻る
        </Link>

        <header className="mb-6">
          <h1 className="bg-gradient-to-br from-foreground via-foreground to-foreground/40 bg-clip-text text-3xl font-bold tracking-tight text-transparent sm:text-4xl">
            新しい変身を作る
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            素材をアップロードして、変身のしかたを選ぶだけ。
          </p>
        </header>

        {prefilling && (
          <div className="mb-4 flex items-center justify-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 text-sm text-primary backdrop-blur">
            <Loader2 className="h-4 w-4 animate-spin" />
            過去の設定を読み込み中...
          </div>
        )}

        {/* === 3 アップロード === */}
        <section className="mb-5 space-y-2.5">
          <div className="relative">
            <CompactSelfieUploader
              userId={userId}
              value={selfie}
              onChange={setSelfie}
            />
            {coachmarkVisible && !selfie && (
              <Coachmark onDismiss={dismissCoachmark} />
            )}
          </div>
          {persisted.mode !== "background" && (
            <BackgroundModeSelector
              userId={userId}
              mode={persisted.backgroundMode}
              onModeChange={(m) => updatePersisted({ backgroundMode: m })}
              asset={background}
              onAssetChange={setBackground}
              slotKey="bg"
            />
          )}
          {showReferenceSlot && (
            <div className="relative">
              <CompactImageUploader
                userId={userId}
                value={reference}
                onChange={setReference}
                label={
                  persisted.mode === "image"
                    ? "なりたい人の画像（必須）"
                    : persisted.mode === "outfit"
                      ? "参考にする服装の画像（必須）"
                      : "参照画像（任意）"
                }
                hint={
                  persisted.mode === "image"
                    ? "写真・AI画像・イラストOK"
                    : persisted.mode === "outfit"
                      ? "服が写った画像を1枚"
                      : "雰囲気を伝える画像を1枚"
                }
                slotKey="ref"
                required={referenceRequired}
              />
              {sampleFlags.reference && reference && (
                <div className="absolute right-2 top-2 z-10">
                  <SampleBadge />
                </div>
              )}
            </div>
          )}
        </section>

        {/* === 声を変える（任意） === */}
        <VoiceChangeOption className="mb-5" />

        {/* === モードタブ === */}
        <section className="mb-4">
          <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            変身モード
          </div>
          <div className="grid grid-cols-4 gap-2">
            {MODE_DEFS.map((m) => {
              const active = persisted.mode === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => updatePersisted({ mode: m.id })}
                  className={cn(
                    "group/tab relative flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-center transition-all",
                    active
                      ? "border-primary/60 bg-primary/10 text-foreground shadow-[0_0_24px_-8px_hsl(var(--primary)/0.6)]"
                      : "border-border/60 bg-card/40 text-muted-foreground hover:border-border hover:bg-card/70 hover:text-foreground",
                  )}
                >
                  <span className="text-xl leading-none" aria-hidden>
                    {m.icon}
                  </span>
                  <span className="text-xs font-semibold">{m.label}</span>
                  <span className="hidden text-[10px] leading-tight text-muted-foreground sm:block">
                    {m.hint}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {/* === モード別入力 === */}
        {persisted.mode === "text" && (
          <div className="group/textarea relative">
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-px rounded-2xl bg-gradient-to-br from-primary/40 via-fuchsia-500/20 to-indigo-500/30 opacity-0 blur transition-opacity duration-500 group-focus-within/textarea:opacity-100"
            />
            <div className="relative overflow-hidden rounded-2xl border border-border/70 bg-card/60 shadow-2xl shadow-black/20 backdrop-blur-xl transition-colors focus-within:border-primary/50">
              {sampleFlags.freeText && persisted.freeText && (
                <div className="absolute right-3 top-3 z-10">
                  <SampleBadge />
                </div>
              )}
              <Textarea
                maxLength={FREE_TEXT_MAX}
                value={persisted.freeText}
                onChange={(e) => updatePersisted({ freeText: e.target.value })}
                placeholder={`どんな自分になりたい？\n\n例：平安時代の貴族の女性に\n例：未来都市のサイバーパンク\n例：白無垢で結婚式の自分`}
                className="min-h-[280px] resize-none border-0 bg-transparent px-6 py-5 text-lg leading-relaxed shadow-none placeholder:text-muted-foreground/60 focus-visible:ring-0 sm:min-h-[320px] sm:text-xl"
              />
              <div className="flex items-center justify-between border-t border-border/50 bg-background/30 px-4 py-2.5 backdrop-blur-sm">
                <button
                  type="button"
                  onClick={() => {
                    setHistoryFilter("text");
                    setHistoryOpen(true);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background/50 px-3 py-1.5 text-xs font-medium text-muted-foreground transition-all hover:border-border hover:bg-background hover:text-foreground"
                >
                  <History className="h-3.5 w-3.5" />
                  過去の生成から
                </button>
                <span
                  className={cn(
                    "font-mono text-[11px] tabular-nums text-muted-foreground/70",
                    persisted.freeText.length > FREE_TEXT_MAX && "text-destructive",
                  )}
                >
                  <span className="text-foreground/80">
                    {persisted.freeText.length}
                  </span>{" "}
                  / {FREE_TEXT_MAX}
                </span>
              </div>
            </div>
            {trimmedFreeText.length > 0 && trimmedFreeText.length < FREE_TEXT_MIN && (
              <p className="mt-2 px-1 text-xs text-muted-foreground">
                {FREE_TEXT_MIN}文字以上入力してください
              </p>
            )}
          </div>
        )}

        {persisted.mode === "age" && (
          <div className="relative space-y-3 rounded-2xl border border-border/70 bg-card/60 p-4 backdrop-blur-xl">
            {sampleFlags.age && (
              <div className="absolute right-3 top-3 z-10">
                <SampleBadge />
              </div>
            )}
            <div>
              <div className="mb-2 flex items-baseline justify-between">
                <span className="text-sm font-semibold">いまの実年齢</span>
                <span className="text-xs text-muted-foreground">必須・0〜120</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={120}
                  value={persisted.currentAge ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "") {
                      updatePersisted({ currentAge: null });
                      return;
                    }
                    const n = Math.max(0, Math.min(120, Math.floor(Number(v) || 0)));
                    // targetAge を実年齢に揃える（範囲内の 5 刻みにスナップ）
                    const opts = getAgeOptions(n);
                    const snapped =
                      opts.find((o) => o >= n) ?? opts[opts.length - 1] ?? n;
                    updatePersisted({ currentAge: n, targetAge: snapped });
                  }}
                  placeholder="例：35"
                  className="h-10 w-28 rounded-lg border border-border/60 bg-background/60 px-3 text-sm font-semibold focus:border-primary focus:outline-none"
                />
                <span className="text-sm text-muted-foreground">歳</span>
              </div>
            </div>

            {persisted.currentAge && persisted.currentAge > 0 ? (
              <div>
                <div className="mb-2 flex items-baseline justify-between">
                  <span className="text-sm font-semibold">なりたい年齢</span>
                  <span className="text-xs text-muted-foreground">
                    {Math.max(0, Math.floor((persisted.currentAge - 10) / 5) * 5)}
                    〜{persisted.currentAge + 60}歳（5歳刻み）
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {getAgeOptions(persisted.currentAge).map((a) => {
                    const active = persisted.targetAge === a;
                    const isCurrent = a === persisted.currentAge;
                    return (
                      <button
                        key={a}
                        type="button"
                        onClick={() => updatePersisted({ targetAge: a })}
                        className={cn(
                          "rounded-full border px-3 py-1.5 text-sm font-semibold transition-all",
                          active
                            ? "border-primary bg-primary text-primary-foreground shadow-[0_0_18px_-6px_hsl(var(--primary)/0.7)]"
                            : isCurrent
                              ? "border-border bg-background/60 text-foreground"
                              : "border-border/60 bg-background/40 text-muted-foreground hover:border-border hover:text-foreground",
                        )}
                      >
                        {a}歳
                        {isCurrent && (
                          <span className="ml-1 text-[10px] opacity-70">いま</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                実年齢を入力すると、−10歳〜+60歳の範囲から選べます。
              </p>
            )}
          </div>
        )}

        {persisted.mode === "background" && (
          <div className="space-y-4 rounded-2xl border border-border/70 bg-card/60 p-4 backdrop-blur-xl">
            <div>
              <p className="mb-2 text-xs font-semibold text-muted-foreground">
                ① プリセットから選ぶ
              </p>
              <div className="flex flex-wrap gap-1.5">
                {BACKGROUND_PRESETS.map((p) => {
                  const active = persisted.freeText === p.description;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => {
                        updatePersisted({ freeText: p.description });
                        setBackground(undefined);
                      }}
                      className={cn(
                        "rounded-full border px-3 py-1.5 text-xs font-semibold transition",
                        active
                          ? "border-primary bg-primary text-primary-foreground shadow-[0_0_18px_-6px_hsl(var(--primary)/0.7)]"
                          : "border-border/60 bg-background/40 text-muted-foreground hover:border-border hover:text-foreground",
                      )}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs font-semibold text-muted-foreground">
                ② または自由に背景を記述
              </p>
              <Textarea
                maxLength={FREE_TEXT_MAX}
                value={persisted.freeText}
                onChange={(e) => {
                  updatePersisted({ freeText: e.target.value });
                  if (background) setBackground(undefined);
                }}
                placeholder={`例：パリの街角、夕暮れの東京タワー、満開のひまわり畑\n\nどんな場所・シーンに置き換えたい？`}
                className="min-h-[120px] resize-none rounded-xl border border-border/60 bg-background/40 px-4 py-3 text-sm"
              />
              <div className="mt-1 flex items-center justify-end">
                <span className="font-mono text-[11px] tabular-nums text-muted-foreground/70">
                  {persisted.freeText.length} / {FREE_TEXT_MAX}
                </span>
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs font-semibold text-muted-foreground">
                ③ または背景画像をアップロード
              </p>
              <CompactImageUploader
                userId={userId}
                value={background}
                onChange={(a) => {
                  setBackground(a);
                  if (a) updatePersisted({ freeText: "" });
                }}
                label="使いたい背景画像（任意）"
                hint="使う場合は ①② は不要"
                slotKey="bg-only"
              />
            </div>

            <p className="text-[11px] text-muted-foreground">
              人物（顔・服装・ポーズ）はそのままで、背景だけを置き換えます。
            </p>
          </div>
        )}

        {/* 任意プロンプト（image / outfit / age） */}
        {persisted.mode !== "text" && persisted.mode !== "background" && (
          <div className="relative mt-3 rounded-2xl border border-border/70 bg-card/60 p-1 backdrop-blur-xl">
            {sampleFlags.prompt && persisted.prompt && (
              <div className="absolute right-3 top-3 z-10">
                <SampleBadge />
              </div>
            )}
            <Textarea
              maxLength={PROMPT_MAX}
              value={persisted.prompt}
              onChange={(e) => updatePersisted({ prompt: e.target.value })}
              placeholder={
                persisted.mode === "image"
                  ? "（任意）追加の指示があれば。例：髪を金髪に、笑顔で"
                  : persisted.mode === "outfit"
                    ? "（任意）追加の指示があれば。例：色は黒で、ジャケットだけ"
                    : "（任意）追加の指示があれば。例：白髪混じり、優しい表情"
              }
              className="min-h-[80px] resize-none border-0 bg-transparent px-4 py-3 text-sm shadow-none placeholder:text-muted-foreground/60 focus-visible:ring-0"
            />
            <div className="flex items-center justify-end border-t border-border/40 px-3 py-1.5">
              <span className="font-mono text-[11px] tabular-nums text-muted-foreground/70">
                {persisted.prompt.length} / {PROMPT_MAX}
              </span>
            </div>
          </div>
        )}

        {/* 動画設定（インライン表示） */}
        <div className="mt-4 rounded-2xl border border-border/60 bg-background/40 p-4">
          <div className="mb-3 flex items-center gap-2">
            <Settings2 className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              動画設定
            </span>
            {settingsCustomized && <Check className="h-3.5 w-3.5 text-primary" />}
          </div>
          <div className="space-y-4">
            <SettingToggle
              label="解像度"
              value={persisted.resolution}
              onChange={(v) => updatePersisted({ resolution: v })}
              options={[
                { v: "720p" as const, label: "720p" },
                { v: "1080p" as const, label: "1080p" },
              ]}
            />
            <SettingToggle
              label="長さ"
              value={persisted.duration}
              onChange={(v) => updatePersisted({ duration: v })}
              options={[
                { v: 5 as const, label: "5秒" },
                { v: 10 as const, label: "10秒" },
                { v: 15 as const, label: "15秒" },
              ]}
            />
            <SettingToggle
              label="音声"
              value={persisted.audio}
              onChange={(v) => updatePersisted({ audio: v })}
              options={[
                { v: false, label: "なし" },
                { v: true, label: "あり (+50%)" },
              ]}
            />
          </div>
        </div>

        {/* フッターアクション */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 px-1">
          <div className="flex flex-wrap items-center gap-2">
            {persisted.mode !== "text" && (
              <button
                type="button"
                onClick={() => {
                  setHistoryFilter(persisted.mode);
                  setHistoryOpen(true);
                }}
                className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background/50 px-3 py-1.5 text-xs font-medium text-muted-foreground transition-all hover:border-border hover:bg-background hover:text-foreground"
              >
                <History className="h-3.5 w-3.5" />
                過去の生成から
              </button>
            )}
          </div>
          <Link
            to="/create/template/gallery"
            className="group/tpl inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-primary"
          >
            テンプレートから探す
            <span className="transition-transform group-hover/tpl:translate-x-0.5">
              →
            </span>
          </Link>
        </div>
      </div>


      {/* sticky bottom 生成バー */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border/50 bg-gradient-to-t from-background via-background/95 to-background/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3.5">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              消費 / 残高
            </div>
            <div className="text-sm">
              <span className="font-bold text-foreground">{totalEstimate}</span>
              <span className="text-muted-foreground">
                cr / {balance ?? "—"}cr
              </span>
            </div>
          </div>
          <Button
            size="lg"
            disabled={!canSubmit}
            onClick={() => setConfirmOpen(true)}
            title={validation.ok ? undefined : validation.reason}
            className="group/btn relative min-w-[200px] overflow-hidden rounded-full shadow-[0_0_40px_-10px_hsl(var(--primary)/0.6)] transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:shadow-none disabled:hover:scale-100"
          >
            <Sparkles className="mr-1.5 h-4 w-4 transition-transform group-hover/btn:rotate-12" />
            変身動画を生成
          </Button>
        </div>
      </div>

      {/* 詳細設定モーダル */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>動画の設定</DialogTitle>
            <DialogDescription>
              解像度・長さ・音声を変更できます
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <SettingToggle
              label="解像度"
              value={persisted.resolution}
              onChange={(v) => updatePersisted({ resolution: v })}
              options={[
                { v: "720p" as const, label: "720p" },
                { v: "1080p" as const, label: "1080p" },
              ]}
            />
            <SettingToggle
              label="長さ"
              value={persisted.duration}
              onChange={(v) => updatePersisted({ duration: v })}
              options={[
                { v: 5 as const, label: "5秒" },
                { v: 10 as const, label: "10秒" },
                { v: 15 as const, label: "15秒" },
              ]}
            />
            <SettingToggle
              label="音声"
              value={persisted.audio}
              onChange={(v) => updatePersisted({ audio: v })}
              options={[
                { v: false, label: "なし" },
                { v: true, label: "あり (+50%)" },
              ]}
            />
          </div>
          <DialogFooter>
            <Button onClick={() => setSettingsOpen(false)}>保存して閉じる</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 生成確認モーダル */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>この内容で生成しますか？</DialogTitle>
            <DialogDescription>
              内容を確認してから「開始する」を押してください。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            <section>
              <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                変身モード
              </div>
              <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-sm">
                {MODE_DEFS.find((m) => m.id === persisted.mode)?.icon}{" "}
                {MODE_DEFS.find((m) => m.id === persisted.mode)?.label}
                {persisted.mode === "age" && ` — ${persisted.targetAge}歳`}
              </div>
            </section>

            {persisted.mode === "text" && (
              <section>
                <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  自由記述
                </div>
                <div className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-sm leading-relaxed">
                  {trimmedFreeText}
                </div>
              </section>
            )}

            {persisted.mode !== "text" && persisted.prompt.trim() && (
              <section>
                <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  追加プロンプト
                </div>
                <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-sm">
                  {persisted.prompt.trim()}
                </div>
              </section>
            )}

            <section className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <PreviewItem label="自撮り動画" icon={<Video className="h-4 w-4 text-primary" />} value={selfie?.name ?? "—"} />
              <PreviewItem label="背景" icon={<ImageIcon className="h-4 w-4 text-primary" />} value={background?.name ?? "（なし）"} />
              {showReferenceSlot && (
                <PreviewItem label="参照画像" icon={<ImageIcon className="h-4 w-4 text-primary" />} value={reference?.name ?? "（なし）"} />
              )}
            </section>

            <section>
              <label htmlFor="tpl-title" className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                動画の名前 <span className="font-normal normal-case">（無記入の場合は生成日時）</span>
              </label>
              <input
                id="tpl-title"
                type="text"
                value={persisted.title}
                onChange={(e) => updatePersisted({ title: e.target.value })}
                maxLength={100}
                placeholder="例：平安貴族に変身"
                className="w-full rounded-lg border border-border/60 bg-background/60 px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </section>

            <section>
              <div className="mb-1.5 flex items-center justify-between">
                <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  動画設定
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmOpen(false);
                    setSettingsOpen(true);
                  }}
                  className="text-xs text-primary hover:underline"
                >
                  変更
                </button>
              </div>
              <dl className="grid grid-cols-3 gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5 text-sm">
                <div>
                  <dt className="text-[10px] text-muted-foreground">解像度</dt>
                  <dd className="font-semibold">{persisted.resolution}</dd>
                </div>
                <div>
                  <dt className="text-[10px] text-muted-foreground">長さ</dt>
                  <dd className="font-semibold">{persisted.duration}秒</dd>
                </div>
                <div>
                  <dt className="text-[10px] text-muted-foreground">音声</dt>
                  <dd className="font-semibold">{persisted.audio ? "あり" : "なし"}</dd>
                </div>
              </dl>
            </section>

            <section className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">消費クレジット</span>
                <span>
                  <strong className="text-base text-foreground">
                    {totalEstimate}
                  </strong>
                  <span className="text-muted-foreground"> cr</span>
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                <span>残高</span>
                <span>{balance ?? "—"} cr</span>
              </div>
            </section>

            <p className="text-xs text-muted-foreground">
              開始後はキャンセル・返金できません。完了まで数分かかります。
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={submitting}
            >
              <X className="mr-1 h-4 w-4" />
              キャンセル
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-1 h-4 w-4" />
              )}
              同意して開始する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RecreateFromFreeformHistoryModal
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        filter={historyFilter}
        onPick={(id) => void prefillFromGeneration(id)}
      />

    </main>
  );
}

/* ---------- Preview item ---------- */
function PreviewItem({
  label,
  icon,
  value,
}: {
  label: string;
  icon: React.ReactNode;
  value: string;
}) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-sm">
        <span className="shrink-0">{icon}</span>
        <span className="truncate">{value}</span>
      </div>
    </div>
  );
}

/* ---------- Setting toggle ---------- */
function SettingToggle<T extends string | number | boolean>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { v: T; label: string }[];
}) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold text-muted-foreground">
        {label}
      </label>
      <div className="inline-flex w-full rounded-md border border-input p-0.5">
        {options.map((o) => (
          <button
            key={String(o.v)}
            type="button"
            onClick={() => onChange(o.v)}
            className={cn(
              "flex-1 rounded px-3 py-1.5 text-sm font-semibold transition",
              value === o.v
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------- Coachmark (quick-start guide popup) ---------- */
function Coachmark({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div
      role="dialog"
      aria-label="自撮り動画のアップロードガイド"
      className="pointer-events-auto absolute left-1/2 top-full z-30 mt-3 w-[min(20rem,calc(100vw-2rem))] -translate-x-1/2 rounded-xl border border-primary/50 bg-primary px-4 py-3 text-primary-foreground shadow-2xl shadow-primary/40"
    >
      <span
        aria-hidden
        className="absolute -top-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-l border-t border-primary/50 bg-primary"
      />
      <p className="text-sm font-semibold leading-snug">
        あなたの自撮り動画をここにアップロード
      </p>
      <p className="mt-1 text-xs leading-snug text-primary-foreground/85">
        このサンプルがあなたに変身します
      </p>
      <div className="mt-2.5 flex justify-end">
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-full bg-primary-foreground/15 px-3 py-1 text-xs font-semibold transition hover:bg-primary-foreground/25"
        >
          わかった
        </button>
      </div>
    </div>
  );
}

/* ---------- Sample prefill badge ---------- */
function SampleBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
      <Sparkles className="h-2.5 w-2.5" />
      サンプル · 自分用に変更可
    </span>
  );
}

function CompactSelfieUploader({
  userId,
  value,
  onChange,
}: {
  userId: string | null;
  value: SelfieAsset | undefined;
  onChange: (a: SelfieAsset | undefined) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file: File) => {
    if (!userId) {
      toast.error("ログインが必要です");
      return;
    }
    if (!VIDEO_TYPES.includes(file.type)) {
      toast.error("動画のみ対応しています (mp4 / mov)");
      return;
    }
    if (file.size > VIDEO_MAX) {
      toast.error(`動画サイズが大きすぎます（最大 ${fmtBytes(VIDEO_MAX)}）`);
      return;
    }
    setUploading(true);
    try {
      const frame = await extractFrameFromVideo(file);
      if (frame.duration > VIDEO_MAX_DURATION + 0.5) {
        toast.error(`${VIDEO_MAX_DURATION}秒以内の動画をご利用ください`);
        setUploading(false);
        return;
      }
      const minSide = Math.min(frame.width, frame.height);
      if (minSide < 700) {
        toast.error(
          `動画の解像度が小さすぎます（短辺 ${minSide}px / 700px 以上が必要）。スマホのカメラで直接撮影した動画をご利用ください。LINE や SNS 経由で受け取った動画は圧縮されていることがあります。`,
          { duration: 8000 },
        );
        setUploading(false);
        return;
      }

      const ts = Date.now();
      const ext = file.name.split(".").pop() ?? "mp4";
      const videoPath = `${userId}/${ts}-selfie.${ext}`;
      const framePath = `${userId}/${ts}-selfie-frame.jpg`;

      const [videoUp, frameUp] = await Promise.all([
        supabase.storage.from("uploads").upload(videoPath, file, {
          contentType: file.type,
          upsert: false,
        }),
        supabase.storage.from("uploads").upload(framePath, frame.blob, {
          contentType: "image/jpeg",
          upsert: false,
        }),
      ]);
      if (videoUp.error) throw videoUp.error;
      if (frameUp.error) throw frameUp.error;

      const [{ data: vsigned }, { data: fsigned }] = await Promise.all([
        supabase.storage.from("uploads").createSignedUrl(videoPath, 60 * 60 * 24),
        supabase.storage.from("uploads").createSignedUrl(framePath, 60 * 60 * 24),
      ]);

      onChange({
        path: videoPath,
        signedUrl: vsigned?.signedUrl ?? "",
        framePath,
        frameSignedUrl: fsigned?.signedUrl ?? "",
        name: file.name,
        size: file.size,
        duration: frame.duration,
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "アップロードに失敗しました");
    } finally {
      setUploading(false);
    }
  };

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-xl border border-primary/40 bg-card/60 px-3.5 py-2.5 text-sm backdrop-blur-md">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
            <Video className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0">
            <div className="truncate text-xs font-medium">{value.name}</div>
            <div className="text-[10px] text-muted-foreground">
              自撮り動画
              {value.size > 0 && ` ・ ${fmtBytes(value.size)}`}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onChange(undefined)}
          className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
          aria-label="動画を削除"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) void handleFile(f);
        }}
        disabled={uploading}
        className={cn(
          "group/up flex w-full items-center justify-center gap-2.5 rounded-xl border border-dashed px-3 py-3 text-sm backdrop-blur-md transition-all",
          dragOver
            ? "border-primary bg-primary/10 text-primary"
            : "border-border/60 bg-card/40 text-muted-foreground hover:border-primary/50 hover:bg-card/70 hover:text-foreground",
        )}
      >
        {uploading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            解析中...
          </>
        ) : (
          <>
            <Video className="h-4 w-4 transition-transform group-hover/up:scale-110" />
            <span className="font-medium">自撮り動画をアップロード（必須）</span>
            <span className="hidden text-xs text-muted-foreground/80 sm:inline">
              mp4/mov・15秒以内
            </span>
          </>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={VIDEO_TYPES.join(",")}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
          e.target.value = "";
        }}
      />
    </>
  );
}

/* ---------- Compact image uploader (background / reference) ---------- */
function CompactImageUploader({
  userId,
  value,
  onChange,
  label,
  hint,
  slotKey,
  required,
}: {
  userId: string | null;
  value: Asset | undefined;
  onChange: (a: Asset | undefined) => void;
  label: string;
  hint: string;
  slotKey: string;
  required?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file: File) => {
    if (!userId) {
      toast.error("ログインが必要です");
      return;
    }
    if (!IMAGE_TYPES.includes(file.type)) {
      toast.error("画像のみ対応しています (jpg / png / webp)");
      return;
    }
    if (file.size > IMAGE_MAX) {
      toast.error(`画像サイズが大きすぎます（最大 ${fmtBytes(IMAGE_MAX)}）`);
      return;
    }
    setUploading(true);
    try {
      const ts = Date.now();
      const ext = file.name.split(".").pop() ?? "jpg";
      const path = `${userId}/${ts}-${slotKey}.${ext}`;
      const up = await supabase.storage.from("uploads").upload(path, file, {
        contentType: file.type,
        upsert: false,
      });
      if (up.error) throw up.error;
      const { data: signed } = await supabase.storage
        .from("uploads")
        .createSignedUrl(path, 60 * 60 * 24);
      onChange({
        path,
        signedUrl: signed?.signedUrl ?? "",
        name: file.name,
        size: file.size,
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "アップロードに失敗しました");
    } finally {
      setUploading(false);
    }
  };

  if (value) {
    return (
      <div
        className={cn(
          "flex items-center justify-between gap-2 rounded-xl border bg-card/60 px-3.5 py-2.5 text-sm backdrop-blur-md",
          required ? "border-primary/40" : "border-border/60",
        )}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
            <ImageIcon className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0">
            <div className="truncate text-xs font-medium">{value.name}</div>
            <div className="text-[10px] text-muted-foreground">
              {label} ・ {fmtBytes(value.size)}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onChange(undefined)}
          className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
          aria-label="画像を削除"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) void handleFile(f);
        }}
        disabled={uploading}
        className={cn(
          "group/up flex w-full items-center justify-center gap-2.5 rounded-xl border border-dashed px-3 py-3 text-sm backdrop-blur-md transition-all",
          dragOver
            ? "border-primary bg-primary/10 text-primary"
            : required
              ? "border-primary/50 bg-card/40 text-muted-foreground hover:border-primary hover:bg-card/70 hover:text-foreground"
              : "border-border/60 bg-card/40 text-muted-foreground hover:border-primary/50 hover:bg-card/70 hover:text-foreground",
        )}
      >
        {uploading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            アップロード中...
          </>
        ) : (
          <>
            <ImageIcon className="h-4 w-4 transition-transform group-hover/up:scale-110" />
            <span className="font-medium">{label}</span>
            <span className="hidden text-xs text-muted-foreground/80 sm:inline">
              {hint}
            </span>
          </>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_TYPES.join(",")}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
          e.target.value = "";
        }}
      />
    </>
  );
}
